import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Mail, Phone, Building, Eye, Trash2, X, Search, ShoppingBag, Calendar, ExternalLink, Ban, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCustomers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // View Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  
  // Delete/Disable confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteAction, setDeleteAction] = useState('delete'); // 'delete' or 'disable'

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/admin/login');
      return;
    }
    
    fetchCustomers();
  }, [navigate]);

  const fetchCustomers = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const openProfileModal = async (customer) => {
    setSelectedCustomer(customer);
    setShowProfileModal(true);
    setLoadingOrders(true);
    
    // Fetch customer's orders
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admin/customers/${customer.id}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerOrders(response.data);
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      setCustomerOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedCustomer(null);
    setCustomerOrders([]);
  };

  const openDeleteConfirm = (customer, action = 'delete') => {
    setSelectedCustomer(customer);
    setDeleteAction(action);
    setShowDeleteConfirm(true);
  };

  const handleDeleteCustomer = async () => {
    const token = localStorage.getItem('token');
    
    try {
      if (deleteAction === 'disable') {
        await axios.put(
          `${API}/admin/customers/${selectedCustomer.id}`,
          { is_active: false },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Customer disabled successfully');
      } else {
        await axios.delete(
          `${API}/admin/customers/${selectedCustomer.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Customer deleted successfully');
      }
      
      setShowDeleteConfirm(false);
      setSelectedCustomer(null);
      fetchCustomers();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to perform action');
    }
  };

  const handleEnableCustomer = async (customer) => {
    const token = localStorage.getItem('token');
    
    try {
      await axios.put(
        `${API}/admin/customers/${customer.id}`,
        { is_active: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Customer enabled successfully');
      fetchCustomers();
    } catch (error) {
      console.error('Error enabling customer:', error);
      toast.error('Failed to enable customer');
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      'paid': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'content_received': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-purple-100 text-purple-800',
      'live': 'bg-emerald-100 text-emerald-800',
      'cancelled': 'bg-red-100 text-red-800',
      'refunded': 'bg-gray-100 text-gray-800',
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-600';
  };

  const filteredCustomers = customers.filter(customer => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(query) ||
      customer.email?.toLowerCase().includes(query) ||
      customer.phone?.toLowerCase().includes(query) ||
      customer.company?.toLowerCase().includes(query) ||
      customer.id.toLowerCase().includes(query)
    );
  });

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-playfair text-4xl font-bold mb-2">Customers</h1>
        <p className="font-manrope text-sm text-gray-600">View and manage registered customers</p>
      </div>

      <div className="mb-6">
        <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Search Customers</label>
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, phone, company..."
            data-testid="customers-search-input"
            className="w-full border border-gray-200 pl-10 pr-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="font-manrope text-gray-600">Loading...</p>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="text-center py-12 border border-gray-200 p-12">
          <p className="font-manrope text-gray-600">
            {searchQuery ? 'No customers match your search' : 'No customers registered yet'}
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <p className="font-manrope text-sm text-gray-600">
              Showing {filteredCustomers.length}{searchQuery && ` of ${customers.length}`} customers
            </p>
          </div>
          
          <div className="border border-gray-200 overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Customer</th>
                  <th className="text-left px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Contact</th>
                  <th className="text-left px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Company</th>
                  <th className="text-left px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Orders</th>
                  <th className="text-left px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Joined</th>
                  <th className="text-left px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Status</th>
                  <th className="text-right px-6 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className={`border-b border-gray-200 hover:bg-gray-50 ${customer.is_active === false ? 'bg-red-50' : ''}`} data-testid={`customer-row-${customer.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-manrope font-semibold text-sm">{customer.name}</div>
                      <div className="font-mono text-xs text-gray-500">{customer.id.substring(0, 8)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 font-manrope text-sm">
                          <Mail className="w-3 h-3 text-gray-400" />
                          {customer.email}
                        </div>
                        {customer.phone && (
                          <div className="flex items-center gap-2 font-manrope text-sm">
                            <Phone className="w-3 h-3 text-gray-400" />
                            {customer.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {customer.company ? (
                        <div className="flex items-center gap-2 font-manrope text-sm">
                          <Building className="w-3 h-3 text-gray-400" />
                          {customer.company}
                        </div>
                      ) : (
                        <span className="font-manrope text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono font-semibold text-sm">{customer.order_count || 0}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-manrope text-sm">
                        {new Date(customer.created_at).toLocaleDateString('en-IN')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {customer.is_active === false ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-manrope rounded">
                          <Ban className="w-3 h-3" /> Disabled
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-manrope rounded">
                          <CheckCircle className="w-3 h-3" /> Active
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openProfileModal(customer)}
                          data-testid={`view-customer-${customer.id}`}
                          className="p-2 border border-gray-200 hover:border-black hover:bg-black hover:text-white transition-all duration-300"
                          title="View Profile"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {customer.is_active === false ? (
                          <button
                            onClick={() => handleEnableCustomer(customer)}
                            data-testid={`enable-customer-${customer.id}`}
                            className="p-2 border border-green-200 text-green-600 hover:bg-green-600 hover:text-white hover:border-green-600 transition-all duration-300"
                            title="Enable Customer"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        ) : (
                          <>
                            {customer.order_count > 0 ? (
                              <button
                                onClick={() => openDeleteConfirm(customer, 'disable')}
                                data-testid={`disable-customer-${customer.id}`}
                                className="p-2 border border-orange-200 text-orange-600 hover:bg-orange-600 hover:text-white hover:border-orange-600 transition-all duration-300"
                                title="Disable Customer"
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => openDeleteConfirm(customer, 'delete')}
                                data-testid={`delete-customer-${customer.id}`}
                                className="p-2 border border-red-200 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-300"
                                title="Delete Customer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* View Customer Profile Modal */}
      {showProfileModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
              <div>
                <h3 className="font-playfair text-xl font-bold">Customer Profile</h3>
                <p className="font-mono text-xs text-gray-500 mt-1">{selectedCustomer.id}</p>
              </div>
              <button 
                onClick={closeProfileModal}
                className="p-1 hover:bg-gray-100 rounded"
                data-testid="close-profile-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Customer Details */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-4">
                  <div>
                    <label className="font-manrope text-xs text-gray-500 uppercase tracking-widest">Name</label>
                    <p className="font-manrope font-semibold mt-1">{selectedCustomer.name}</p>
                  </div>
                  <div>
                    <label className="font-manrope text-xs text-gray-500 uppercase tracking-widest">Email</label>
                    <p className="font-manrope mt-1 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {selectedCustomer.email}
                    </p>
                  </div>
                  <div>
                    <label className="font-manrope text-xs text-gray-500 uppercase tracking-widest">Phone</label>
                    <p className="font-manrope mt-1 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {selectedCustomer.phone || '-'}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="font-manrope text-xs text-gray-500 uppercase tracking-widest">Company</label>
                    <p className="font-manrope mt-1 flex items-center gap-2">
                      <Building className="w-4 h-4 text-gray-400" />
                      {selectedCustomer.company || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="font-manrope text-xs text-gray-500 uppercase tracking-widest">GSTIN</label>
                    <p className="font-mono mt-1">{selectedCustomer.gstin || '-'}</p>
                  </div>
                  <div>
                    <label className="font-manrope text-xs text-gray-500 uppercase tracking-widest">Joined</label>
                    <p className="font-manrope mt-1 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {new Date(selectedCustomer.created_at).toLocaleDateString('en-IN', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Customer Orders */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingBag className="w-5 h-5" />
                  <h4 className="font-playfair text-lg font-bold">Orders ({selectedCustomer.order_count || 0})</h4>
                </div>
                
                {loadingOrders ? (
                  <div className="text-center py-8">
                    <p className="font-manrope text-gray-500">Loading orders...</p>
                  </div>
                ) : customerOrders.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-gray-200">
                    <p className="font-manrope text-gray-500">No orders yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {customerOrders.map((order) => (
                      <div 
                        key={order.id} 
                        className="border border-gray-200 p-4 hover:border-gray-300 transition-colors"
                        data-testid={`order-${order.id}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-manrope font-semibold">{order.publication_name || 'Unknown Publication'}</p>
                            <p className="font-manrope text-sm text-gray-600">{order.package_name || 'Unknown Package'}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-manrope rounded ${getStatusBadge(order.order_status)}`}>
                            {order.order_status?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <div className="flex items-center gap-4">
                            <span className="font-mono text-xs text-gray-500">{order.id.substring(0, 8)}</span>
                            <span className="font-manrope text-gray-600">
                              {new Date(order.created_at).toLocaleDateString('en-IN')}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-mono font-semibold">₹{order.total_amount?.toLocaleString('en-IN')}</span>
                            {order.live_url && (
                              <a 
                                href={order.live_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                                title="View live article"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete/Disable Confirmation Modal */}
      {showDeleteConfirm && selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className={`w-16 h-16 ${deleteAction === 'disable' ? 'bg-orange-100' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                {deleteAction === 'disable' ? (
                  <Ban className="w-8 h-8 text-orange-600" />
                ) : (
                  <Trash2 className="w-8 h-8 text-red-600" />
                )}
              </div>
              <h3 className="font-playfair text-xl font-bold mb-2">
                {deleteAction === 'disable' ? 'Disable Customer' : 'Delete Customer'}
              </h3>
              <p className="font-manrope text-sm text-gray-600">
                {deleteAction === 'disable' 
                  ? 'This customer will be disabled and cannot log in. Their orders will be preserved.'
                  : 'Are you sure you want to delete this customer? This action cannot be undone.'
                }
              </p>
              <p className="font-manrope text-sm font-semibold mt-2">
                {selectedCustomer.name}
              </p>
              <p className="font-mono text-xs text-gray-500">
                {selectedCustomer.email}
              </p>
              {deleteAction === 'delete' && selectedCustomer.order_count > 0 && (
                <p className="font-manrope text-sm text-orange-600 mt-2">
                  ⚠️ This customer has {selectedCustomer.order_count} orders. Consider disabling instead.
                </p>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                data-testid="cancel-delete-btn"
                className="flex-1 px-6 py-3 border border-gray-200 font-manrope text-xs uppercase tracking-widest hover:border-black transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCustomer}
                data-testid="confirm-delete-btn"
                className={`flex-1 px-6 py-3 font-manrope text-xs uppercase tracking-widest transition-all duration-300 ${
                  deleteAction === 'disable'
                    ? 'bg-orange-600 text-white hover:bg-orange-700'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {deleteAction === 'disable' ? 'Disable Customer' : 'Delete Customer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCustomers;